/* eslint-disable max-len */
import React, { useState, Fragment, useEffect, useContext } from "react";
import { Dialog, Transition } from "@headlessui/react";

import { Spinner } from "..";

import { loginWithGoogle, login } from "../../helpers/magic";
import { analytics } from "../../helpers/analytics";
import { UserContext } from "../../context/userContext";
import { validateEmail } from "../../helpers/shared";
import AppLogo from "../../assets/images/app-logo.png";
import JewelryLogin from "../../assets/images/jewelry-login.png";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialEmail?: string;
  isEmailLocked?: boolean;
  hideGoogle?: boolean;
};

const SignIn = ({
  isOpen,
  onClose,
  onSuccess,
  initialEmail = "",
  isEmailLocked = false,
  hideGoogle = false,
}: Props) => {
  const [email, setEmail] = useState(initialEmail);
  const [isEmailValid, setEmailValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState("");

  const { user } = useContext(UserContext);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmailLocked) return;
    const value = e.target.value;
    setEmail(value);
    setAuthMessage("");
    if (value.length <= 1) {
      setEmailValid(true);
    }
  };

  const handleClick = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const isValid = Boolean(validateEmail(normalizedEmail));
    setEmailValid(isValid);

    if (!isValid) {
      analytics.track("Login Validation Failed", {
        method: "email",
        reason: "invalid_email",
      });
    }

    if (isValid) {
      setIsLoading(true);
      analytics.track("Login Started", { method: "email" });
      try {
        await login(normalizedEmail);
        setIsLoading(false);
        setAuthMessage("Check your inbox for a secure sign-in link.");
        if (!isEmailLocked) {
          setEmail("");
        }
        analytics.track("Magic Link Requested", { method: "email" });
      } catch (e) {
        setIsLoading(false);
        const message = e instanceof Error ? e.message : "";
        setAuthMessage(message || "Something went wrong. Please try again.");
        analytics.track("Login Failed", {
          method: "email",
          error_message: message || String(e),
        });
        // eslint-disable-next-line no-console
        console.log(e);
      }
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleClick();
  };

  useEffect(() => {
    if (isOpen && user) {
      setIsGoogleLoading(false);
      analytics.track("Login Completed", {
        method: "existing_session",
        has_premium: user.hasPremium,
      });
      onSuccess();
    }
  }, [isOpen, user, onSuccess]);

  useEffect(() => {
    if (isOpen) {
      analytics.track("Auth Modal Viewed");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setEmailValid(true);
      setAuthMessage("");
    }
  }, [initialEmail, isOpen]);

  const handleGoogleClick = async () => {
    setIsGoogleLoading(true);
    setAuthMessage("");
    analytics.track("Login Started", { method: "google" });
    try {
      await loginWithGoogle();
    } catch (e) {
      setIsGoogleLoading(false);
      setAuthMessage("Google sign-in is unavailable right now.");
      analytics.track("Login Failed", {
        method: "google",
        error_message: e instanceof Error ? e.message : String(e),
      });
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-20 inset-0 overflow-y-auto" onClose={onClose}>
        <div className="flex min-h-screen items-center justify-center p-6 text-center md:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-[#07090a]/82 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="relative grid min-h-[540px] w-full max-w-[920px] grid-cols-[1.02fr_0.98fr] overflow-hidden rounded-[22px] bg-[#0d0f10] text-left align-middle shadow-[0_28px_80px_rgba(0,0,0,0.5)] transition-all md:min-h-0 md:max-w-[440px] md:grid-cols-1"
            >
              <button
                type="button"
                aria-label="Close sign in"
                className="absolute right-5 top-5 z-[2] flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white hover:bg-white/16"
                onClick={() => {
                  analytics.track("Auth Modal Closed", {
                    source: "close_button",
                  });
                  onClose();
                }}
              >
                &times;
              </button>

              <div className="flex flex-col p-11 md:p-7">
                <div className="inline-flex h-10 w-10">
                  <img src={AppLogo} alt="" className="h-10 w-10 rounded-[11px]" />
                </div>

                <div className="mt-10 md:mt-8">
                  <Dialog.Title className="m-0 text-[26px] font-bold leading-[1.2] tracking-normal text-[#f7f7f2]">
                    Welcome to Tools for Procreate
                  </Dialog.Title>
                  <p className="mt-2 text-sm font-medium leading-sm text-[#8c918f]">
                    Log in to continue exploring brushes, tools, and downloads.
                  </p>
                </div>

                <div className="mt-10 grid w-full max-w-[420px] gap-4">
                  {!hideGoogle && (
                    <>
                      <button
                        type="button"
                        className="flex h-[54px] w-full items-center justify-center gap-3 rounded-[13px] border border-[#232729] bg-[#101314] text-sm font-semibold text-[#f4f4ef] hover:border-[#383d3f] hover:bg-[#15191a]"
                        onClick={handleGoogleClick}
                      >
                        {!isGoogleLoading ? (
                          <>
                            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
                            </svg>
                            <span>Continue with Google</span>
                          </>
                        ) : (
                          <Spinner theme="light" />
                        )}
                      </button>

                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#555b5a]">
                        <div className="h-px bg-[#202425]" />
                        <span>or</span>
                        <div className="h-px bg-[#202425]" />
                      </div>
                    </>
                  )}

                  <form className="grid gap-3" onSubmit={handleSubmit}>
                    <label className="sr-only" htmlFor="sign-in-email">
                      Email address
                    </label>
                    <input
                      id="sign-in-email"
                      className={`h-[52px] rounded-[13px] border bg-[#090b0c] px-4 text-[15px] font-medium text-[#f7f7f2] outline-none placeholder:text-[#5e6462] focus:border-[#757d79] ${
                        isEmailValid ? "border-[#232729]" : "border-main-red-900"
                      }`}
                      placeholder="you@company.com"
                      type="email"
                      autoComplete="email"
                      required
                      readOnly={isEmailLocked}
                      aria-readonly={isEmailLocked}
                      value={email}
                      onChange={handleInputChange}
                    />
                    {!isEmailValid && (
                      <p className="text-xs leading-xs text-main-red-500">
                        Incorrect email address
                      </p>
                    )}
                    <button
                      type="submit"
                      className="flex h-[54px] w-full items-center justify-center rounded-[13px] border border-[#232729] bg-[#101314] text-sm font-semibold text-[#f4f4ef] hover:border-[#383d3f] hover:bg-[#15191a]"
                    >
                      {!isLoading ? <span>Send sign-in link</span> : <Spinner theme="light" />}
                    </button>
                  </form>

                  {authMessage && (
                    <p className="m-0 text-[13px] leading-[1.5] text-[#c9d2cb]">
                      {authMessage}
                    </p>
                  )}
                </div>

                <p className="mt-auto max-w-[390px] pt-7 text-[11px] leading-[1.6] text-[#676d6b] md:mt-8">
                  By continuing, you agree to the{" "}
                  <a
                    className="font-semibold text-[#aeb7b3] underline underline-offset-4 hover:text-[#f4f4ef]"
                    href="https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>{" "}
                  and acknowledge the{" "}
                  <a
                    className="font-semibold text-[#aeb7b3] underline underline-offset-4 hover:text-[#f4f4ef]"
                    href="https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>

              <div className="relative overflow-hidden bg-[#1a0f1f] md:hidden">
                <img
                  src={JewelryLogin}
                  alt="Jewelry illustration"
                  className="absolute inset-0 h-full w-full object-cover object-[50%_24%]"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(13,15,16,0)_26%,rgba(13,15,16,0.28)_56%,rgba(13,15,16,0.9)_100%)]" />
                <div className="absolute bottom-9 left-9 right-9 text-[#fff7ef]">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-[#ffc7e6]">
                    Tools for Procreate
                  </p>
                  <p className="mt-2 max-w-[340px] text-lg font-semibold leading-[1.35] tracking-normal text-[#fff7ef]">
                    Unlock Procreate brushes, jewelry tools, and artist-ready assets.
                  </p>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export { SignIn };
